import React from "react";
import { cn } from "@/lib/utils";
import { useRefStore } from "@/stores/uiStore";
import { priorityStyle, initials } from "@/utils/formatters";

export const PriorityBadge = ({ priority, size = "sm" }) => (
  <span className={cn("inline-flex items-center justify-center rounded font-semibold tabular-nums",
    size === "sm" ? "h-5 min-w-[1.5rem] px-1 text-xs" : "h-6 min-w-[1.75rem] px-1.5 text-sm", priorityStyle(priority))}
    title={`Priorita ${priority}`}>{priority}</span>
);

export const FlagBadges = ({ flags = [], onRemove, compact = false }) => {
  const flagTypeById = useRefStore((s) => s.flagTypeById);
  if (!flags.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {flags.map((f) => {
        const ft = flagTypeById(f.flag_type_id || f) || {};
        return (
          <span key={f.id || f} title={f.note || ft.name}
            className={cn("inline-flex items-center gap-1 rounded-full border px-1.5 text-xs leading-5", compact && "px-1")}
            style={{ borderColor: ft.color, color: ft.color, backgroundColor: `${ft.color}12` }}>
            <span>{ft.icon}</span>{!compact && <span>{ft.name}</span>}
            {onRemove && f.id && <button onClick={(e) => { e.stopPropagation(); onRemove(f); }} className="ml-0.5 opacity-60 hover:opacity-100">×</button>}
          </span>
        );
      })}
    </span>
  );
};

export const TagBadges = ({ tagIds = [], onRemove }) => {
  const tagById = useRefStore((s) => s.tagById);
  return (
    <span className="inline-flex flex-wrap gap-1">
      {tagIds.map((id) => { const t = tagById(id); if (!t) return null; return (
        <span key={id} className="inline-flex items-center gap-1 rounded px-1.5 text-xs leading-5 text-white" style={{ backgroundColor: t.color }}>
          {t.name}{onRemove && <button onClick={() => onRemove(id)} className="opacity-70 hover:opacity-100">×</button>}
        </span>); })}
    </span>
  );
};

export const StatusPill = ({ name, color }) => name ? (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 border border-gray-200 px-2 text-xs leading-5 text-gray-700">
    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color || "#9CA3AF" }} />{name}
  </span>) : <span className="text-sm text-gray-400">—</span>;

export const UserAvatar = ({ name, size = 6 }) => (
  <span className={cn("inline-flex items-center justify-center rounded-full bg-brand-100 text-brand-700 font-medium", `h-${size} w-${size}`, size <= 6 ? "text-xs" : "text-sm")} title={name}>{initials(name || "?")}</span>
);

export const UserName = ({ id, fallback = "—" }) => {
  const userName = useRefStore((s) => s.userName);
  return <span>{id ? userName(id) : fallback}</span>;
};

export const EmptyState = ({ title, hint, action }) => (
  <div className="flex flex-col items-center justify-center py-10 text-center">
    <p className="text-base font-medium text-gray-700">{title}</p>
    {hint && <p className="mt-1 text-sm text-gray-400">{hint}</p>}
    {action && <div className="mt-3">{action}</div>}
  </div>
);

export const Section = ({ title, count, action, children, className }) => (
  <section className={cn("rounded-lg border border-gray-100 bg-white", className)}>
    <header className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}{count !== undefined && <span className="ml-1.5 text-gray-400 font-normal">{count}</span>}</h3>
      {action}
    </header>
    <div className="p-3">{children}</div>
  </section>
);

export const Spinner = ({ className }) => <div className={cn("h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-brand-600", className)} />;

export const KBtn = ({ children, className, active, ...props }) => (
  <button type="button" {...props} className={cn("rounded-md border px-2.5 py-1 text-sm transition-colors",
    active ? "border-brand-600 bg-brand-600 text-white" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50", className)}>{children}</button>
);

const fmtWebDate = (d) => { if (!d) return ""; const x = new Date(d); return `${x.getDate()}. ${x.getMonth() + 1}. ${x.getFullYear()}`; };

/** Příznak webináře odvozený z leadu: účast = červený (žhavý), registrace bez účasti = oranžový (vlažný). */
export const WebinarBadge = ({ w, compact = false, className }) => {
  if (!w) return null;
  const attended = w.attended === true;
  const st = w.school_type ? ` ${w.school_type}` : "";
  const date = fmtWebDate(w.date);
  const title = w.title && !compact ? ` · ${w.title}` : "";
  const label = attended ? `Byl/a na webináři${st}${date ? ` · ${date}` : ""}${title}`
    : `Registrace webinář${st}${date ? ` · ${date}` : ""} · ${w.attended === false ? "nepřišel/a" : "účast zatím nenahrána"}${title}`;
  const tip = `${attended ? "ÚČAST na webináři" : w.attended === false ? "Registrace na webinář – NEPŘIŠEL/A" : "Registrace na webinář – účast zatím nenahrána"}${st}${date ? ` · ${date}` : ""}${w.title ? `\n${w.title}` : ""}${w.person ? `\nRegistrace: ${w.person}${w.email ? ` (${w.email})` : ""}` : ""}`;
  return (
    <span title={tip} data-testid={attended ? "webinar-badge-attended" : "webinar-badge-registered"}
      className={cn("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-bold", compact ? "whitespace-nowrap" : "max-w-full", attended ? "bg-red-600 text-white" : "bg-amber-100 text-amber-900", className)}>
      {attended ? "🎓" : "📝"} {label}
    </span>
  );
};

/** Z příznaků kontaktu (d.flags) vybere webinářové a seřadí: účast první, pak novější. */
export const webinarFlags = (flags, flagTypes) => {
  const codes = Object.fromEntries((flagTypes || []).filter((ft) => ft.code).map((ft) => [ft.id, ft.code]));
  return (flags || []).filter((f) => codes[f.flag_type_id]?.startsWith("webinar_") && f.meta)
    .map((f) => ({ ...f.meta, flag_id: f.id }))
    .sort((a, b) => (b.attended === true) - (a.attended === true) || new Date(b.date || 0) - new Date(a.date || 0));
};
